import { traceError } from '@/utils/trace-error.ts';

const MAX_PENDING_ITEMS = 3;
const MAX_TEXT_LENGTH = 400;
const MAX_CHUNK_LENGTH = 180;
const DUPLICATE_WINDOW_MS = 5000;
const POST_CANCEL_KICK_DELAY_MS = 150;

export type ReadAloudGender = 'female' | 'male';

export interface ReadAloudOptions {
  volume: number;
  ownerId: string;
  gender?: ReadAloudGender;
  priority?: boolean;
  interrupt?: boolean;
  onUnavailable?: () => void;
}

interface ReadAloudItem {
  chunks: string[];
  volume: number;
  ownerId: string;
  gender?: ReadAloudGender;
  priority: boolean;
  onUnavailable?: () => void;
}

let pending: ReadAloudItem[] = [];
let activeItem: ReadAloudItem | null = null;
let activeUtterance: SpeechSynthesisUtterance | null = null;
let blockedUntilInteraction = false;
const recentTexts = new Map<string, number>();

export const isReadAloudSupported = () =>
  typeof window !== 'undefined' && 'speechSynthesis' in window;

const sanitizeForSpeech = (text: string) => {
  const stripped = text
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (stripped.length <= MAX_TEXT_LENGTH) {
    return stripped;
  }

  const cut = stripped.slice(0, MAX_TEXT_LENGTH);
  const lastSpace = cut.lastIndexOf(' ');
  return lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
};

const splitLongSentence = (sentence: string) => {
  const parts: string[] = [];
  let rest = sentence;

  while (rest.length > MAX_CHUNK_LENGTH) {
    const cut = rest.slice(0, MAX_CHUNK_LENGTH);
    const lastSpace = cut.lastIndexOf(' ');
    const splitAt = lastSpace > 0 ? lastSpace : MAX_CHUNK_LENGTH;
    parts.push(rest.slice(0, splitAt).trim());
    rest = rest.slice(splitAt).trim();
  }

  if (rest) {
    parts.push(rest);
  }

  return parts;
};

const splitIntoChunks = (text: string) => {
  const sentences = text
    .split(/(?<=[.!?])\s+/)
    .flatMap((sentence) => splitLongSentence(sentence));
  const chunks: string[] = [];

  for (const sentence of sentences) {
    const last = chunks[chunks.length - 1];
    if (last && last.length + sentence.length + 1 <= MAX_CHUNK_LENGTH) {
      chunks[chunks.length - 1] = `${last} ${sentence}`;
    } else {
      chunks.push(sentence);
    }
  }

  return chunks;
};

const isDuplicateText = (text: string) => {
  const now = Date.now();

  for (const [key, spokenAt] of recentTexts) {
    if (now - spokenAt > DUPLICATE_WINDOW_MS) {
      recentTexts.delete(key);
    }
  }

  const lastSpokenAt = recentTexts.get(text);
  recentTexts.set(text, now);
  return lastSpokenAt !== undefined && now - lastSpokenAt < DUPLICATE_WINDOW_MS;
};

const FEMALE_VOICE_PREFERENCES = [
  'samantha',
  'microsoft aria',
  'google us english',
  'microsoft zira',
];

const MALE_VOICE_PREFERENCES = [
  'alex',
  'microsoft guy',
  'google uk english male',
  'microsoft david',
];

const normalizeLang = (lang: string) => lang.toLowerCase().replace('_', '-');

const getEnglishVoice = (gender?: ReadAloudGender) => {
  const voices = window.speechSynthesis.getVoices();
  const english = voices.filter((voice) =>
    normalizeLang(voice.lang).startsWith('en'),
  );

  if (gender) {
    const preferences =
      gender === 'female' ? FEMALE_VOICE_PREFERENCES : MALE_VOICE_PREFERENCES;
    for (const preference of preferences) {
      const match = english.find((voice) =>
        voice.name.toLowerCase().includes(preference),
      );
      if (match) {
        return match;
      }
    }
  }

  return english.find((voice) => voice.localService) ?? english[0] ?? null;
};

const blockSpeechUntilInteraction = () => {
  if (blockedUntilInteraction) {
    return;
  }
  blockedUntilInteraction = true;
  traceError(
    new Error('Read aloud blocked until the user interacts with the page'),
  );

  const unblock = () => {
    blockedUntilInteraction = false;
    window.removeEventListener('pointerdown', unblock);
    window.removeEventListener('keydown', unblock);
  };
  window.addEventListener('pointerdown', unblock);
  window.addEventListener('keydown', unblock);
};

const clearActiveSpeech = () => {
  pending = [];
  activeItem = null;
  activeUtterance = null;
};

const speakChunks = (item: ReadAloudItem, index: number) => {
  if (index >= item.chunks.length) {
    speakNext();
    return;
  }

  const utterance = new SpeechSynthesisUtterance(item.chunks[index]);
  utterance.lang = 'en-US';
  utterance.volume = Math.max(0, Math.min(1, item.volume));

  const voice = getEnglishVoice(item.gender);
  if (voice) {
    utterance.voice = voice;
  }

  utterance.onend = () => {
    if (activeUtterance !== utterance) {
      return;
    }
    speakChunks(item, index + 1);
  };

  utterance.onerror = (event) => {
    if (activeUtterance !== utterance) {
      return;
    }

    if (event.error === 'not-allowed') {
      blockSpeechUntilInteraction();
      clearActiveSpeech();
      item.onUnavailable?.();
      return;
    }

    if (event.error !== 'canceled' && event.error !== 'interrupted') {
      traceError(new Error(`Read aloud failed: ${event.error}`));
    }
    speakNext();
  };

  activeUtterance = utterance;
  window.speechSynthesis.speak(utterance);
};

const speakNext = () => {
  const item = pending.shift();
  if (!item) {
    activeItem = null;
    activeUtterance = null;
    return;
  }
  activeItem = item;
  speakChunks(item, 0);
};

const kick = () => {
  const engineIdle =
    !window.speechSynthesis.speaking && !window.speechSynthesis.pending;
  if (!activeUtterance || engineIdle) {
    speakNext();
  }
};

export const readAloud = (text: string, options: ReadAloudOptions) => {
  if (!isReadAloudSupported() || blockedUntilInteraction) {
    return false;
  }

  const sanitized = sanitizeForSpeech(text);
  if (!sanitized) {
    return false;
  }

  if (!options.interrupt && isDuplicateText(sanitized)) {
    return true;
  }

  const item: ReadAloudItem = {
    chunks: splitIntoChunks(sanitized),
    volume: options.volume,
    ownerId: options.ownerId,
    gender: options.gender,
    priority: options.priority ?? false,
    onUnavailable: options.onUnavailable,
  };

  if (options.interrupt) {
    pending = [item];
    if (activeUtterance || window.speechSynthesis.speaking) {
      activeItem = null;
      activeUtterance = null;
      window.speechSynthesis.cancel();
      setTimeout(kick, POST_CANCEL_KICK_DELAY_MS);
    } else {
      speakNext();
    }
    return true;
  }

  pending.push(item);
  if (pending.length > MAX_PENDING_ITEMS) {
    const evictIndex = pending.findIndex((queued) => !queued.priority);
    pending.splice(evictIndex === -1 ? 0 : evictIndex, 1);
  }

  kick();
  return true;
};

export const stopReadAloud = (ownerId?: string) => {
  if (!isReadAloudSupported()) {
    return;
  }

  if (ownerId === undefined) {
    pending = [];
  } else {
    pending = pending.filter((item) => item.ownerId !== ownerId);
    if (activeItem && activeItem.ownerId !== ownerId) {
      return;
    }
  }

  activeItem = null;
  activeUtterance = null;
  window.speechSynthesis.cancel();

  if (pending.length) {
    setTimeout(kick, POST_CANCEL_KICK_DELAY_MS);
  }
};
